import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getCarteiraExposicao,
  getCarteiraFrescor,
  getCnpjs,
} from "../../services/monitoramentoSerasa";
import type {
  CarteiraExposicao,
  CarteiraFrescor,
  SnapshotSerasaLinha,
} from "../../models/monitoramentoSerasa/types";
import { normalizeCnpjParaApi } from "./cnpj";
import {
  ApiErrorPanel,
  formatBRL,
  formatDataHora,
  PainelRodape,
  SegmentoChip,
  TendenciaChip,
} from "./shared";

export const MonitoramentoCarteira = () => {
  const navigate = useNavigate();
  const [linhas, setLinhas] = useState<SnapshotSerasaLinha[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [busca, setBusca] = useState("");
  const [segmento, setSegmento] = useState("");
  const [ordenarPor, setOrdenarPor] = useState("valorTotalAnotacoes");
  const [precisaRevisao, setPrecisaRevisao] = useState(false);
  /** Filtros efetivos na última busca (evita refetch a cada tecla na busca). */
  const [filtroTick, setFiltroTick] = useState(0);
  const [frescor, setFrescor] = useState<CarteiraFrescor | null>(null);
  const [exposicao, setExposicao] = useState<CarteiraExposicao | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    void filtroTick;
    setLoading(true);
    setErro("");
    try {
      const [pg, fr, ex] = await Promise.all([
        getCnpjs({
          pagina: page + 1,
          tamanhoPagina: pageSize,
          busca: busca || undefined,
          segmento:
            segmento === "" ? undefined : (parseInt(segmento, 10) as 0 | 1 | 2),
          ordenarPor: ordenarPor || undefined,
          precisaRevisao: precisaRevisao ? true : undefined,
        }),
        getCarteiraFrescor().catch(() => null),
        getCarteiraExposicao().catch(() => null),
      ]);
      setLinhas(pg.itens ?? []);
      setTotal(pg.totalRegistros ?? 0);
      setFrescor(fr);
      setExposicao(ex);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : "Erro ao carregar a carteira.";
      setErro(msg);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    busca,
    segmento,
    ordenarPor,
    precisaRevisao,
    filtroTick,
  ]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const paretoData = (exposicao?.top10PorValorAnotacoes ?? [])
    .slice(0, 10)
    .map((r) => ({
      nome:
        (r.razaoSocial || r.cnpj || "").slice(0, 22) +
        ((r.razaoSocial || "").length > 22 ? "…" : ""),
      valor: r.valorTotalAnotacoes ?? 0,
    }));

  const onRowClick = (row: SnapshotSerasaLinha) => {
    const n = normalizeCnpjParaApi(row.cnpj);
    if (n) navigate(`/monitoramento/fornecedor/${n}`);
  };

  return (
    <Box>
      <Typography variant="h4" component="h2" fontWeight={700} gutterBottom>
        Carteira monitorada
      </Typography>

      {erro ? (
        <Box sx={{ mb: 2 }}>
          <ApiErrorPanel message={erro} onRetry={carregar} />
        </Box>
      ) : null}

      {paretoData.length > 0 ? (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Top 10 por exposição (anotações)
            </Typography>
            <Box sx={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={paretoData} layout="vertical" margin={{ left: 8 }}>
                  <XAxis type="number" tickFormatter={(v) => formatBRL(v)} />
                  <YAxis type="category" dataKey="nome" width={120} tick={{ fontSize: 11 }} />

                  <RTooltip formatter={(v: any) => formatBRL(Number(v ?? 0))} />
                  <Bar dataKey="valor" fill="var(--dv-1)" radius={[0, 4, 4, 0]} />

                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Busca (razão social ou CNPJ)"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Segmento"
                value={segmento}
                onChange={(e) => setSegmento(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="0">Saudável (0)</MenuItem>
                <MenuItem value="1">Atenção (1)</MenuItem>
                <MenuItem value="2">Crítico (2)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Ordenar por"
                value={ordenarPor}
                onChange={(e) => setOrdenarPor(e.target.value)}
              >
                <MenuItem value="valorTotalAnotacoes">Exposição (anotações)</MenuItem>
                <MenuItem value="data">Última consulta</MenuItem>
                <MenuItem value="razao">Razão social</MenuItem>
                <MenuItem value="score">Piores primeiro (score)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={precisaRevisao}
                    onChange={(_, c) => setPrecisaRevisao(c)}
                  />
                }
                label="Somente com revisão pendente"
              />
              <Button
                sx={{ ml: 2 }}
                variant="contained"
                onClick={() => {
                  setPage(0);
                  setFiltroTick((t) => t + 1);
                }}
              >
                Aplicar filtros
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Card} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fornecedor</TableCell>
              <TableCell>Última consulta</TableCell>
              <TableCell>Segmento</TableCell>
              <TableCell align="right">Valor anotações</TableCell>
              <TableCell align="right">Protestos</TableCell>
              <TableCell>Tendência</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : linhas.map((row) => (
                  <TableRow
                    key={row.cnpj || row.razaoSocial}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => onRowClick(row)}
                  >
                    <TableCell>
                      <Typography fontWeight={500}>
                        {row.razaoSocial || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.cnpj}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDataHora(row.dataConsulta)}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={(row.motivosSegmento || []).join(" · ") || "—"}
                      >
                        <span>
                          <SegmentoChip segmento={row.segmentoCarteira} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {formatBRL(row.valorTotalAnotacoes)}
                    </TableCell>
                    <TableCell align="right">
                      {row.quantidadeProtestos ?? "—"} /{" "}
                      {formatBRL(row.valorProtestos)}
                    </TableCell>
                    <TableCell>
                      <TendenciaChip tendencia={row.tendenciaVsAnterior} />
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas"
        />
      </TableContainer>

      {!loading && linhas.length === 0 ? (
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Nenhum registro com os filtros atuais. Limpe filtros ou ajuste a busca.
        </Typography>
      ) : null}

      <PainelRodape
        versaoRegras={frescor?.versaoRegras}
        labelDadosAte={
          frescor?.ultimaDataConsultaGlobal
            ? `Última consulta global na carteira: ${formatDataHora(
                frescor.ultimaDataConsultaGlobal
              )}`
            : frescor?.geradoEmUtc
            ? `Frescor gerado em ${formatDataHora(frescor.geradoEmUtc)}`
            : undefined
        }
      />
    </Box>
  );
};
